import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ImageWithDate } from '../../../../types/contactLog';
import { CloseIcon, DownloadIcon } from '../../../../components/Icons';
import { canUseShareSheet, isAbortError, prefetchFiles, savePrefetchedPhotos } from './photoSave';
import {
  Overlay,
  Card,
  Header,
  HeaderDate,
  CloseButton,
  Photo,
  Footer,
  LongPressHint,
  SaveButton,
  FADE_DURATION_MS,
} from './PhotoDetailModal.styled';

interface PhotoDetailModalProps {
  // 表示対象の写真。null のとき非表示
  image: ImageWithDate | null;
  onClose: () => void;
}

const PhotoDetailModalComponent: React.FC<PhotoDetailModalProps> = ({ image, onClose }) => {
  // fadeOut を見せてからアンマウントするためのフラグ
  const [isClosing, setIsClosing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // 共有シートへ渡す File。先読みが未完了/失敗のあいだは null
  const [file, setFile] = useState<File | null>(null);
  // 共有シート経由の保存が成立しないと判明したか（先読み失敗・共有シート失敗のいずれも）
  const [isShareUnavailable, setIsShareUnavailable] = useState(false);

  // File が揃うまでは保存させない。未完了のまま保存に進むと await が入り共有シートが
  // 使えなくなるため、専用の状態を持たず File の有無から導出して取りこぼしを防ぐ
  const isPreparing = file === null;

  // 共有シートが使えない環境では保存ボタンを出さず、端末の長押し保存へ案内する
  const showsLongPressHint = !canUseShareSheet() || isShareUnavailable;

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, FADE_DURATION_MS);
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (!image) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [image, handleKeyDown]);

  // 共有シートは transient activation を要求するため、表示時点で File を用意しておく。
  // 共有シートが使えない環境では長押し保存に委ねるので、先読みは通信の無駄になるため行わない
  useEffect(() => {
    setFile(null);
    setIsShareUnavailable(false);
    if (!image || !canUseShareSheet()) return;
    // 写真を切り替えたら不要になった取得を打ち切る（cache: 'reload' で毎回フル通信になるため）
    let canceled = false;
    const controller = new AbortController();
    prefetchFiles([image], controller.signal)
      .then(([prefetched]) => {
        if (canceled) return;
        // 共有シートにファイルを渡せるかは File を得てからでないと判定できない。
        // 渡せない環境で「押しても保存できないボタン」を出さないよう、ここで案内へ倒す
        if (!navigator.canShare?.({ files: [prefetched] })) {
          setIsShareUnavailable(true);
          return;
        }
        setFile(prefetched);
      })
      .catch((err) => {
        // 中断は打ち切りなので通知しない。先読み失敗はその時点で共有シートに渡せないと
        // 確定するため、無駄なタップを挟まず案内へ切り替える
        if (canceled || isAbortError(err)) return;
        console.error('写真の先読みに失敗しました', err);
        setIsShareUnavailable(true);
      });
    return () => {
      canceled = true;
      controller.abort();
    };
  }, [image]);

  const handleSave = useCallback(async () => {
    if (!image || isSaving || isPreparing) return;
    setIsSaving(true);
    try {
      // savePrefetchedPhotos は共有シート呼び出しまで await を挟まない。前段に await を置かないこと
      const outcome = await savePrefetchedPhotos(file ? [file] : null, [image]);
      // キャンセルは失敗ではないため、保存ボタンをそのまま残してやり直せるようにする
      if (outcome === 'needsLongPress' || outcome === 'needsOneByOne') {
        setIsShareUnavailable(true);
      }
    } catch (err) {
      console.error('写真の保存に失敗しました', err);
      setIsShareUnavailable(true);
    } finally {
      setIsSaving(false);
    }
  }, [image, file, isSaving, isPreparing]);

  // fadeOut を再生し終えるまではアンマウントしない
  if (!image && !isClosing) return null;
  // 閉じアニメーション中も直前の画像を保持して表示する
  const currentImage = image;
  if (!currentImage) return null;

  const dateLabel = currentImage.date ? currentImage.date.replaceAll('-', '/') : '';

  return createPortal(
    <Overlay
      $isClosing={isClosing}
      $duration={FADE_DURATION_MS}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      {/* カード内クリックでは閉じない */}
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          {dateLabel && <HeaderDate>{dateLabel}</HeaderDate>}
          <CloseButton onClick={handleClose} aria-label="閉じる">
            <CloseIcon />
          </CloseButton>
        </Header>
        <Photo src={currentImage.url} alt={dateLabel ? `${dateLabel}の写真` : '写真'} />
        <Footer>
          {showsLongPressHint ? (
            <LongPressHint role="status">写真長押しで保存できます</LongPressHint>
          ) : (
            <SaveButton onClick={handleSave} disabled={isSaving || isPreparing}>
              <DownloadIcon />
              {isPreparing ? '準備中…' : '写真を保存する'}
            </SaveButton>
          )}
        </Footer>
      </Card>
    </Overlay>,
    document.body
  );
};

export const PhotoDetailModal = React.memo(PhotoDetailModalComponent);

export default PhotoDetailModal;
